import { ReactNode, useEffect, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { resizeZemChargePopupWindow, ZEM_CHARGE_DESKTOP_DIALOG_MAX_WIDTH, ZEM_CHARGE_POPUP_INITIAL_HEIGHT, ZEM_CHARGE_POPUP_MODE } from '@zep/common/routes';
import { ChevronLeftIcon } from '@zep/icons';
import { useViewportDevice } from '@zep/react';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogPanel, IconContainer } from '@zep/ui';
import { useChargeZem } from '../ZemChargeContext';
import S from './ChargeContainer.module.scss';
export const ChargeContainer = (props: ChargeContainerProps) => {
  const {
    isMobile
  } = useViewportDevice();
  const router = useRouter();
  const isMatches = useMediaQuery({
    maxWidth: 768
  });
  const isZemChargePopup = useMemo(() => {
    const queryString = router.asPath.split('?')[1]?.split('#')[0] ?? '';
    return new URLSearchParams(queryString).get('mode') === ZEM_CHARGE_POPUP_MODE;
  }, [router.asPath]);
  const dialogClassName = isZemChargePopup ? `${S.dialog} ${S.dialog_popup}` : S.dialog;
  useEffect(() => {
    if (!isZemChargePopup) return;
    const animationFrameId = window.requestAnimationFrame(() => {
      const panel = document.querySelector<HTMLElement>(`.${S.popup_panel}`);
      if (!panel) return;
      resizeZemChargePopupWindow({
        width: ZEM_CHARGE_DESKTOP_DIALOG_MAX_WIDTH,
        height: ZEM_CHARGE_POPUP_INITIAL_HEIGHT
      });
    });
    return () => window.cancelAnimationFrame(animationFrameId);
  });
  return <Dialog maxWidth={isZemChargePopup || !isMatches ? `${ZEM_CHARGE_DESKTOP_DIALOG_MAX_WIDTH}px` : '400px'} height={isMobile && !isZemChargePopup ? '100%' : 'auto'} onClose={() => null} open drawer={!isZemChargePopup} className={dialogClassName} data-zem-charge-popup={isZemChargePopup ? 'true' : undefined} data-sentry-element="Dialog" data-sentry-component="ChargeContainer" data-sentry-source-file="ChargeContainer.tsx">
      <DialogPanel className={isZemChargePopup ? S.popup_panel : undefined} data-sentry-element="DialogPanel" data-sentry-source-file="ChargeContainer.tsx">
        <DialogHeader className={isZemChargePopup ? 'min-[480px]:!px-6 min-[480px]:!py-4' : undefined} leftSlot={props.backStep &&
      // @ts-ignore
      <IconContainer type="button" onClick={() => props.backStep()}>
                <ChevronLeftIcon width={20} height={20} aria-label="back" />
              </IconContainer>} data-sentry-element="DialogHeader" data-sentry-source-file="ChargeContainer.tsx">
          {props.title}
        </DialogHeader>
        <DialogContent className={isZemChargePopup ? 'min-[480px]:!px-6 min-[480px]:!pb-6 min-[480px]:!pt-0' : undefined} data-sentry-element="DialogContent" data-sentry-source-file="ChargeContainer.tsx">
          {props.content}
        </DialogContent>
        <DialogFooter className={isZemChargePopup ? `${S.dialog_footer} min-[480px]:!px-6 min-[480px]:!pb-6 min-[480px]:!pt-0` : S.dialog_footer} data-sentry-element="DialogFooter" data-sentry-source-file="ChargeContainer.tsx">
          {props.footer ? props.footer : <ChargeZemFooter action={props.mainAction} />}
        </DialogFooter>
      </DialogPanel>
    </Dialog>;
};
export const ChargeZemCompleteFooter = ({
  action
}: {
  action: () => void;
}) => {
  const {
    isMobile
  } = useViewportDevice();
  const {
    t
  } = useTranslation(['manage']);
  return <div className={S.footer} style={{
    justifyContent: 'flex-end'
  }} data-sentry-component="ChargeZemCompleteFooter" data-sentry-source-file="ChargeContainer.tsx">
      {!isMobile && <div style={{
      flex: 5
    }} />}
      <div style={{
      flex: 5,
      width: ' 100%'
    }}>
        <Button isFullWidth onClick={action} data-sentry-element="Button" data-sentry-source-file="ChargeContainer.tsx">
          {t('manage:charge.complete.apply')}
        </Button>
      </div>
    </div>;
};
const ChargeZemFooter = ({
  action
}: {
  action: () => void;
}) => {
  const {
    enabledPaymentButton,
    selectedZemInfo
  } = useChargeZem();
  const {
    t
  } = useTranslation(['manage']);
  const totalZem = selectedZemInfo.totalCashMoney ?? selectedZemInfo.addCashMoney + selectedZemInfo.addCashMoneyPromotion + selectedZemInfo.addCashMoneyDesktop;
  return <div className={S.footer} data-sentry-component="ChargeZemFooter" data-sentry-source-file="ChargeContainer.tsx">
      <div style={{
      flex: 5
    }} className={S.footer_left} dangerouslySetInnerHTML={{
      __html: t('manage:donate_charge_zem_description', {
        won: `<b>${selectedZemInfo.localeString}</b>`,
        zem: `<b>${totalZem} ZEM</b>`
      })
    }}></div>
      <div style={{
      flex: 5,
      width: '100%'
    }}>
        <Button isFullWidth disabled={!enabledPaymentButton} onClick={action} data-sentry-element="Button" data-sentry-source-file="ChargeContainer.tsx">
          {selectedZemInfo.localeString}{' '}
          {t('manage:charge.paymentSelect.payment')}
        </Button>
      </div>
    </div>;
};
export type ChargeContainerProps = {
  title: string;
  backStep?: () => void;
  mainAction: () => void;
  content: ReactNode;
  footer?: ReactNode;
};